import { Badge, Button, DataTable, Form, Modal } from 'shared/components/ui';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Save, ArrowBack, Edit, Delete, Upload, CloudUpload, Chat, GetApp } from '@mui/icons-material';
import { procesadoresAPI, equiposAPI, practicasAPI, adjuntosAPI, turnosAPI, observacionesAPI } from '../../../../core/api/api';
import { DocumentViewer, ToastContainer } from '../../../../shared/components';
import Header from '../../../../shared/components/layout/Header';
import Footer from '../../../../shared/components/layout/Footer';
import { useAuth } from '../../../../core/context/AuthContext';
import { calcularEdad } from '../../../../shared/utils';
import './EditarPractica.css';

const EditarPractica = ({ turno, onVolver, onGuardar}) => {
  const { user } = useAuth();
  const isMedico = user?.role === 'Medico';
  const [formData, setFormData] = useState({
    // Datos del paciente
    apellido: '',
    nombre: '',
    tipoDni: 'DNI',
    dni: '',
    fechaNacimiento: '',
    direccion: '',
    codigoPostal: '',
    localidad: '',
    correo: '',
    celular: '',
    obraSocial: '',
    plan: '',
    peso: '',
    talle: '',
    fechaVenAutorizacion: '',
    medico: '',
    
    // Datos del turno
    fechaInicio: '',
    fechaFin: '',
    fechaEntrega: '',
    tipoEstudio: '',
    centro: '',
    envioEstudios: '',
    diagnostico: '',
    observaciones: '',
    
    // Especialista derivador
    derivador: '',
    especialidad: '',
    localidadDerivador: '',
    
    // Informe técnico
    estadoEstudio: '',
    equipo: '',
    equipoCPAP: '',
    motivoObservacion: '',
    
    // Datos de la práctica
    tipoMaquina: 'A',
    tipoModelo: 'L',
    tipoInforme: 'B',
    canales: '4',
    microestructuraSueno: '',
    suenoLentoN3: '',
    latenciaInicioSuenoREM: '0',
    cicloSueno: '0',
    suenoNREM: '',
    txtNrem16: '',
    txtRem16: '',
    tiempoTotalSueno: '0',
    oximetriaDigital: '',
    basal: '',
    maxima: '',
    minima: '', 
    promedio: '',
    idoBMC: '',
    reboteSuenoREM: '',
    horarioInicio: '',
    equipoUtilizado: '',
    tipoMascara: '',
    talleEquipo: '',
    presionInicial: '4',
    presionMaxima: '',
    presionRegistro: '',
    presionTerapeutica: '',
    presionPromedio: '',
    tolerancia: '',
    efectividad: '',
    horasEfectivas: '',
    totalHorasSugeridas: '',
    eventosRespiratorios: '',
    apneasObstructivas: '0',
    apneasMixtas: '0',
    apneasCentrales: '0',
    hipopneas: '0',
    totalApneasHipopneas: '0',
    indiceApneas: '0',
    aumentoEsfuerzoVentilatorio: '0',
    indiceEsfuerzoVentilatorio: '0',
    indiceDisturbioRespiratorioGlobal: '0',
    asociadoFragmentosuenoAumento: '',
    asociadosExtrasistolesVentricularesAumento: '',
    movimientosPiernas: '',
    txtMovimientosPiernas: '0',
    indiceMovimientosPiernas: '0',
    actividadEKG: '',
    posicion: 'V',
    ronquidos: '',
    parasomnias: '',
    sindromeApneasHipopneas: '',
    asociadoFragmentosueno: '',
    turnoGenerado: '',
    
    // Estado del turno
    idEstadoTurno: '',
    procesador: '',
    subEstado: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [toast, setToast] = useState(null);
  const [procesadores, setProcesadores] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [adjuntos, setAdjuntos] = useState([]);
  const [adjuntosTecnicos, setAdjuntosTecnicos] = useState([]);
  const [adjuntosEeg, setAdjuntosEeg] = useState([]);
  const [descargandoEeg, setDescargandoEeg] = useState(null);
  const [estadosTurno, setEstadosTurno] = useState([]);
  const [observaciones, setObservaciones] = useState([]);
  const [nuevaObservacion, setNuevaObservacion] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adjuntoToDelete, setAdjuntoToDelete] = useState(null);
  const [showDeletePracticaModal, setShowDeletePracticaModal] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [estadosTurnoLog, setEstadosTurnoLog] = useState([]);
  const [especialistaDerivador, setEspecialistaDerivador] = useState(null);
  const fileInputRef = useRef(null);
  const informeInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    
    const cargarDatos = async () => {
      try {  
        const [procesadoresRes, estadosRes] = await Promise.all([
          procesadoresAPI.getProcesadores(),
          turnosAPI.getEstadosSupervisor()
        ]);
        
        if (isMounted) {
          setProcesadores(procesadoresRes.data || []);
          setEstadosTurno(estadosRes.data || []);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error al cargar datos:', error);
          setProcesadores([]);
          setEstadosTurno([]);
        }
      }
    };
    cargarDatos();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Load equipment only when needed (CPAP is selected)
  useEffect(() => {
    if (formData.tipoInforme === 'C' && equipos.length === 0) {
      let isMounted = true;
      
      const cargarEquipos = async () => {
        try {
          const response = await equiposAPI.getEquipos();
          if (isMounted) {
            const equiposCPAP = (response.data || []).filter(equipo => equipo.tipo === 'C');
            setEquipos(equiposCPAP);
          }
        } catch (error) {
          if (isMounted) {
            console.error('Error al cargar equipos:', error);
            setEquipos([]);
          }
        }
      };
      cargarEquipos();
      
      return () => { 
        isMounted = false;
      };
    }
  }, [formData.tipoInforme, equipos.length]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (turno) {
      const loadTurnoData = async () => {
        console.log(turno)
        const baseData = {
          // Datos del paciente
          apellido: turno.persona?.apellido || '',
          nombre: turno.persona?.nombre || '',
          tipoDni: turno.persona?.tipoDni || 'DNI',
          dni: turno.persona?.dni || '',
          fechaNacimiento: turno.persona?.fechaNacimiento ? 
            new Date(turno.persona.fechaNacimiento).toISOString().split('T')[0] : '',
          direccion: turno.persona?.direccion || '',
          codigoPostal: turno.persona?.codigoPostal || '',
          localidad: turno.persona?.localidad.localidad || '',
          correo: turno.persona?.mail || '',
          celular: turno.persona?.telCelular || '',
          obraSocial: turno.persona?.obraSocial?.obraSocial || '',
          plan: turno.persona?.plan || '',
          peso: turno.peso || '',
          medico: turno.medico?.medico1 || '',
          talle: turno.talle || '',
          fechaVenAutorizacion: turno.fechaVenAutorizacion ? 
            new Date(turno.fechaVenAutorizacion).toISOString().split('T')[0] : '',
          
          // Datos del turno
          fechaInicio: turno.horario?.inicio ? 
            turno.horario.inicio.slice(0, 16) : '',
          fechaFin: turno.horario?.fin ? 
            turno.horario.fin.slice(0, 16) : '',
          fechaEntrega: turno.fechaEntrega ? 
            new Date(turno.fechaEntrega).toISOString().split('T')[0] : '',
          tipoEstudio: turno.tipoEstudio?.tipoEstudio || '',
          centro: turno.centro?.centro || '',
          envioEstudios: turno.persona?.envioEstudio || '',
          diagnostico: turno.diagnostico || '',
          observaciones: turno.observacion || '',
          
          // Informe técnico
          estadoEstudio: turno.realizo || '',
          equipo: turno.idEquipoNavigation?.equipo || turno.idEquipoNavigation?.equipo1 || '',
          equipoCPAP: turno.idEquipoCpapNavigation?.equipo || turno.idEquipoCpapNavigation?.equipo1 || '',
          motivoObservacion: turno.motivoObservacion || '',
          
          // Estado del turno
          estadosTurno: turno.estadosTurno || '',
          idEstadoTurno: turno.idEstadoTurno || '',
          procesador: turno.idProcesador || '',
          subEstado: turno.subEstado || '',
          tecnico: turno.medico?.medico1 || turno.tecnico || '',
          turnoGenerado: turno.usrAlta || '',
          requisitos: turno.requisitos || []
        };
        
        // Cargar adjuntos existentes
        try {
          const adjuntosResponse = await adjuntosAPI.getAdjuntos(turno.idTurno, 'I');
          setAdjuntos(adjuntosResponse.data || []);
        } catch (error) {
          console.warn('Error al cargar adjuntos:', error); 
          setAdjuntos([]);
        }
        
        // Cargar adjuntos técnicos
        try {
          const adjuntosTecnicosResponse = await adjuntosAPI.getAdjuntos(turno.idTurno, 'T');
          setAdjuntosTecnicos(adjuntosTecnicosResponse.data || []);
        } catch (error) {
          console.warn('Error al cargar adjuntos técnicos:', error); 
          setAdjuntosTecnicos([]);
        }

        // Cargar adjuntos EEG (tipo E, descargados automáticamente desde Dropbox)
        try {
          const adjuntosEegResponse = await adjuntosAPI.getAdjuntos(turno.idTurno, 'E');
          setAdjuntosEeg(adjuntosEegResponse.data || []);
        } catch (error) {
          console.warn('Error al cargar adjuntos EEG:', error);
          setAdjuntosEeg([]);
        }
        
        // Cargar observaciones
        try {
          const observacionesResponse = await observacionesAPI.getObservaciones(turno.idTurno);
          setObservaciones(observacionesResponse.data || []);
        } catch (error) {
          console.warn('Error al cargar observaciones:', error);
          setObservaciones([]);
        }
        
        // Cargar estados del turno
        try {
          const estadosResponse = await turnosAPI.getEstadosTurno(turno.idTurno);
          setEstadosTurnoLog(estadosResponse.data || []);
        } catch (error) {
          console.warn('Error al cargar estados del turno:', error);
          setEstadosTurnoLog([]);
        }
        
        // Cargar especialista derivador
        if (turno.idDerivador) {
          try {
            const derivadorResponse = await turnosAPI.getEspecialistaDerivador(turno.idDerivador);
            setEspecialistaDerivador(derivadorResponse.data);
          } catch (error) {
            console.warn('Error al cargar especialista derivador:', error);
            setEspecialistaDerivador(null);
          }
        }
        
        try {
          const practiceResponse = await practicasAPI.obtenerPractica(turno.idTurno);
          const practiceData = practiceResponse.data;
          if (practiceData) {
            setFormData({
              ...baseData,
              // Map practice data to form fields
              tipoMaquina: practiceData.tipoMaquina || 'A',
              tipoModelo: practiceData.tipoModelo || 'L', 
              tipoInforme: practiceData.tipoInforme || 'B',
              canales: practiceData.canales?.toString() || '4',
              microestructuraSueno: practiceData.microestructuraSeno || '',
              tiempoTotalSueno: practiceData.timepoTotalSueno?.toString() || '0',
              suenoLentoN3: practiceData.suenoLentoN3 || '',
              latenciaInicioSuenoREM: practiceData.latenciaRem?.toString() || '0',
              cicloSueno: practiceData.ciclosSueno?.toString() || '0',
              suenoNREM: practiceData.suenoNrem || '',
              txtNrem16: practiceData.textoNrem16 || '',
              txtRem16: practiceData.textoRem16 || '',
              oximetriaDigital: (practiceData.oximetriaBasal || practiceData.oximetriaMaxima || practiceData.oximetriaMinima || practiceData.oximetriaPromedio) ? 'S' : '',
              basal: practiceData.oximetriaBasal?.toString() || '',
              maxima: practiceData.oximetriaMaxima?.toString() || '',
              minima: practiceData.oximetriaMinima?.toString() || '',
              promedio: practiceData.oximetriaPromedio?.toString() || '',
              reboteSuenoREM: practiceData.reboteSuenoRem || '',
              horarioInicio: practiceData.horaInicio ? new Date(practiceData.horaInicio).toTimeString().slice(0, 5) : '',
              equipoUtilizado: practiceData.idEquipoCPAP?.toString() || '',
              tipoMascara: practiceData.tipoMascara || '',
              talleEquipo: practiceData.talle || '',
              presionInicial: practiceData.presionInicial?.toString() || '4',
              presionMaxima: practiceData.presionMaxima?.toString() || '',
              presionRegistro: practiceData.presionRegistro?.toString() || '',
              presionTerapeutica: (practiceData.presionTerapeutica || practiceData.PresionTerapeutica)?.toString() || '',
              presionPromedio: practiceData.presionPromedio?.toString() || '',
              tolerancia: practiceData.tolerancia || '',
              efectividad: practiceData.efectividad || '',
              horasEfectivas: practiceData.totalHorasEfectivas ? new Date(practiceData.totalHorasEfectivas).toLocaleTimeString('en-GB', { hour12: false }).slice(0, 5) : '',
              eventosRespiratorios: (practiceData.apneasObstruidas || practiceData.apneasMixtas || practiceData.apneasCentrales || practiceData.hipopneas) ? 'S' : '',
              apneasObstructivas: practiceData.apneasObstruidas?.toString() || '0',
              apneasMixtas: practiceData.apneasMixtas?.toString() || '0',
              apneasCentrales: practiceData.apneasCentrales?.toString() || '0',
              hipopneas: practiceData.hipopneas?.toString() || '0',
              aumentoEsfuerzoVentilatorio: practiceData.aumentoEsfuerzoVentilatorio?.toString() || '0',
              asociadoFragmentosuenoAumento: practiceData.asociadoExtrasistoleVentricularesAumento || '',
              asociadosExtrasistolesVentricularesAumento: practiceData.asociadoExtrasistoleVentriculares || '',
              movimientosPiernas: (practiceData.movimientoPeriodicoPiernas && practiceData.movimientoPeriodicoPiernas > 0) ? 'S' : 'N',
              txtMovimientosPiernas: practiceData.movimientoPeriodicoPiernas?.toString() || '0',
              actividadEKG: practiceData.actividadEkg || '',
              posicion: practiceData.posicion || 'V',
              ronquidos: practiceData.ronquidos || '',
              parasomnias: practiceData.parasomnias || '',
              asociadoFragmentosueno: practiceData.asociadoFragmentoSueno || '',
              sindromeApneasHipopneas: practiceData.sindromeApneasHipopneas || '',
              asociadosExtrasistolesVentriculares: practiceData.asociadoExtrasistoleVentriculares || '',
              idoBMC: practiceData.ido || practiceData.idoBMC || '',
              equipo: practiceData.idEquipoNavigation?.equipo || practiceData.idEquipoNavigation?.equipo1 || '',
              equipoCPAP: turno.idEquipoCpapNavigation?.equipo || turno.idEquipoCpapNavigation?.equipo1 || '',
            });
            
            // Calculate indices after setting the data
            const apneasCalculations = calculateApneasHipopneas({
              ...baseData,
              apneasCentrales: practiceData.apneasCentrales?.toString() || '0',
              apneasMixtas: practiceData.apneasMixtas?.toString() || '0',
              apneasObstructivas: practiceData.apneasObstruidas?.toString() || '0',
              hipopneas: practiceData.hipopneas?.toString() || '0',
              tiempoTotalSueno: practiceData.timepoTotalSueno?.toString() || '0',
              aumentoEsfuerzoVentilatorio: practiceData.aumentoEsfuerzoVentilatorio?.toString() || '0'
            });
            
            const legCalculations = calculateMovimientosPiernas({
              txtMovimientosPiernas: practiceData.movimientoPeriodicoPiernas?.toString() || '0',
              tiempoTotalSueno: practiceData.timepoTotalSueno?.toString() || '0'
            });
            
            setFormData({
              ...baseData,
              tipoMaquina: practiceData.tipoMaquina || 'A',
              tipoModelo: practiceData.tipoModelo || 'L',
              tipoInforme: practiceData.tipoInforme || 'B',
              canales: practiceData.canales?.toString() || '4',
              microestructuraSueno: practiceData.microestructuraSeno || '',
              tiempoTotalSueno: practiceData.timepoTotalSueno?.toString() || '0',
              suenoLentoN3: practiceData.suenoLentoN3 || '',
              latenciaInicioSuenoREM: practiceData.latenciaRem?.toString() || '0',
              cicloSueno: practiceData.ciclosSueno?.toString() || '0',
              suenoNREM: practiceData.suenoNrem || '',
              txtNrem16: practiceData.textoNrem16 || '',
              txtRem16: practiceData.textoRem16 || '',
              oximetriaDigital: (practiceData.oximetriaBasal || practiceData.oximetriaMaxima || practiceData.oximetriaMinima || practiceData.oximetriaPromedio) ? 'S' : '',
              basal: practiceData.oximetriaBasal?.toString() || '',
              maxima: practiceData.oximetriaMaxima?.toString() || '',
              minima: practiceData.oximetriaMinima?.toString() || '',
              promedio: practiceData.oximetriaPromedio?.toString() || '',
              reboteSuenoREM: practiceData.reboteSuenoRem || '',
              horarioInicio: practiceData.horaInicio ? new Date(practiceData.horaInicio).toTimeString().slice(0, 5) : '',
              equipoUtilizado: practiceData.idEquipoCPAP?.toString() || '',
              tipoMascara: practiceData.tipoMascara || '',
              talleEquipo: practiceData.talle || '',
              presionInicial: practiceData.presionInicial?.toString() || '4',
              presionMaxima: practiceData.presionMaxima?.toString() || '',
              presionRegistro: practiceData.presionRegistro?.toString() || '',
              presionTerapeutica: (practiceData.presionTerapeutica || practiceData.PresionTerapeutica)?.toString() || '',
              presionPromedio: practiceData.presionPromedio?.toString() || '',
              tolerancia: practiceData.tolerancia || '',
              efectividad: practiceData.efectividad || '',
              horasEfectivas: practiceData.totalHorasEfectivas ? new Date(practiceData.totalHorasEfectivas).toLocaleTimeString('en-GB', { hour12: false }).slice(0, 5) : '',
              eventosRespiratorios: (practiceData.apneasObstruidas || practiceData.apneasMixtas || practiceData.apneasCentrales || practiceData.hipopneas) ? 'S' : '',
              apneasObstructivas: practiceData.apneasObstruidas?.toString() || '0',
              apneasMixtas: practiceData.apneasMixtas?.toString() || '0',
              apneasCentrales: practiceData.apneasCentrales?.toString() || '0',
              hipopneas: practiceData.hipopneas?.toString() || '0',
              aumentoEsfuerzoVentilatorio: practiceData.aumentoEsfuerzoVentilatorio?.toString() || '0',
              asociadoFragmentosuenoAumento: practiceData.asociadoExtrasistoleVentricularesAumento || '',
              asociadosExtrasistolesVentricularesAumento: practiceData.asociadoExtrasistoleVentriculares || '',
              movimientosPiernas: (practiceData.movimientoPeriodicoPiernas && practiceData.movimientoPeriodicoPiernas > 0) ? 'S' : 'N',
              txtMovimientosPiernas: practiceData.movimientoPeriodicoPiernas?.toString() || '0',
              actividadEKG: practiceData.actividadEkg || '',
              posicion: practiceData.posicion || 'V',
              ronquidos: practiceData.ronquidos || '',
              parasomnias: practiceData.parasomnias || '',
              asociadoFragmentosueno: practiceData.asociadoFragmentoSueno || '',
              sindromeApneasHipopneas: practiceData.sindromeApneasHipopneas || '',
              asociadoExtrasistoleVentriculares: practiceData.asociadoExtrasistoleVentriculares || '',
              idoBMC: practiceData.ido || practiceData.idoBMC || '',
              ...apneasCalculations,
              ...legCalculations
            });
            setReportGenerated(true);
              
            // Generate DOCX and convert to PDF like GrillaDatosPracticas
            try {
              const reportType = practiceData.tipoMaquina === 'P' 
                ? (practiceData.tipoInforme === 'C' ? 3 : 2)
                : (practiceData.tipoInforme === 'C' ? 4 : 1);
              const reportResponse = await practicasAPI.generarInforme(turno.idTurno, reportType);
               
              if (reportResponse.data) {
                // Convert DOCX to PDF using the same endpoint as GrillaDatosPracticas
                const docxBlob = new Blob([reportResponse.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                const pdfResponse = await practicasAPI.convertirDocxAPdf(docxBlob, `Informe_${turno.idTurno}.docx`);
                
                if (pdfResponse.data) {
                  const pdfUrl = URL.createObjectURL(pdfResponse.data);
                  setPreviewUrl(pdfUrl);
                }
              }
            } catch (reportError) {
              console.warn('Could not generate report preview:', reportError);
            }
          } else {
            // No existing practice data, use defaults
            setFormData({
              ...baseData,
              tipoMaquina: 'A',
              tipoModelo: 'L',
              tipoInforme: 'B',
              canales: '4',
              posicion: 'V',
              latenciaInicioSuenoREM: '0',
              cicloSueno: '0',
              tiempoTotalSueno: '0',
              basal: '0',
              maxima: '0',
              minima: '0',
              promedio: '0',
              apneasObstructivas: '0',
              apneasMixtas: '0',
              apneasCentrales: '0',
              hipopneas: '0',
              totalApneasHipopneas: '0',
              indiceApneas: '0',
              aumentosEsfuerzoVentilatorio: '0',
              txtMovimientosPiernas: '0',
              presionInicial: '4'
            });
          }
        } catch (error) {
          // If error loading practice data, use defaults
          setFormData({
            ...baseData,
            tipoMaquina: 'A',
            tipoModelo: 'L',
            tipoInforme: 'B',
            canales: '4',
            posicion: 'V',
            latenciaInicioSuenoREM: '0',
            cicloSueno: '0',
            tiempoTotalSueno: '0',
            basal: '',
            maxima: '',
            minima: '',
            promedio: '',
            apneasObstructivas: '0',
            apneasMixtas: '0',
            apneasCentrales: '0',
            hipopneas: '0',
            totalApneasHipopneas: '0',
            indiceApneas: '0',
            aumentosEsfuerzoVentilatorio: '0',
            txtMovimientosPiernas: '0',
            presionInicial: '4'
          });
        }
      };
      
      loadTurnoData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turno]);

  const calculateApneasHipopneas = useCallback((data) => {
    const n1 = parseFloat(data.apneasCentrales) || 0;
    const n2 = parseFloat(data.apneasMixtas) || 0;
    const n3 = parseFloat(data.apneasObstructivas) || 0;
    const n4 = parseFloat(data.hipopneas) || 0;
    const tiempoSueno = parseFloat(data.tiempoTotalSueno) || 0;
    const aumentoVentilatorio = parseFloat(data.aumentoEsfuerzoVentilatorio) || 0;
    
    const total = n1 + n2 + n3 + n4;
    let indice = 0;
    let indiceEsfuerzo = 0;
    let indiceGlobal = 0;
    
    if (tiempoSueno > 0) {
      indice = parseFloat((total * 60 / tiempoSueno).toFixed(2));
      indiceEsfuerzo = parseFloat((aumentoVentilatorio * 60 / tiempoSueno).toFixed(2));
      indiceGlobal = parseFloat(((total + aumentoVentilatorio) * 60 / tiempoSueno).toFixed(2));
    }
    
    // Calcular síndrome según el índice
    let sindromeValue = 'N';
    if (indice >= 5 && indice < 15) {
      sindromeValue = 'L';
    } else if (indice >= 15 && indice < 30) {
      sindromeValue = 'M';
    } else if (indice >= 30) {
      sindromeValue = 'S';
    }
    
    return {
      totalApneasHipopneas: total.toString(),
      indiceApneas: indice.toString(),
      indiceEsfuerzoVentilatorio: indiceEsfuerzo.toString(),
      indiceDisturbioRespiratorioGlobal: (aumentoVentilatorio !== 0 ? indiceGlobal : indice).toString(),
      sindromeApneasHipopneas: sindromeValue
    };
  }, []);

  const calculateMovimientosPiernas = useCallback((data) => {
    const movimiento = parseFloat(data.txtMovimientosPiernas) || 0;
    const tiempoSueno = parseFloat(data.tiempoTotalSueno) || 0;
    
    let indice = 0;
    if (tiempoSueno > 0) {
      indice = parseFloat((movimiento / (tiempoSueno / 60)).toFixed(2));
      // Validate result
      if (!isFinite(indice) || isNaN(indice)) {
        indice = 0;
      }
    }
    
    return {
      indiceMovimientosPiernas: indice.toString()
    };
  }, []);

  const validateRequiredFields = useCallback(() => {
    // Campos siempre obligatorios
    if (!formData.tipoMaquina || !formData.tipoMaquina.trim()) {
      showToast('Tipo de Equipo es obligatorio', 'error');
      return false;
    }
    if (!formData.tipoInforme || !formData.tipoInforme.trim()) {
      showToast('Tipo de Informe es obligatorio', 'error');
      return false;
    }
    
    // Validar campos de oximetría si está seleccionado
    if (formData.oximetriaDigital === 'S') {
      if (!formData.basal || !formData.basal.trim()) {
        showToast('Complete el campo Basal %', 'error');
        return false;
      }
      if (!formData.maxima || !formData.maxima.trim()) {
        showToast('Complete el campo Máxima %', 'error');
        return false;
      }
      if (!formData.minima || !formData.minima.trim()) {
        showToast('Complete el campo Mínima %', 'error');
        return false;
      }
      if (!formData.promedio || !formData.promedio.trim()) {
        showToast('Complete el campo Promedio %', 'error');
        return false;
      }
    }
    
    // Campos obligatorios según tipo de máquina
    if (formData.tipoMaquina === 'A') {
      if (!formData.canales || !formData.canales.trim()) {
        showToast('Canales es obligatorio', 'error');
        return false;
      }
      if (!formData.microestructuraSueno || !formData.microestructuraSueno.trim()) {
        showToast('Microestructura del Sueño es obligatorio', 'error');
        return false;
      }
      if (!formData.suenoLentoN3 || !formData.suenoLentoN3.trim()) {
        showToast('Sueño lento N3 es obligatorio', 'error');
        return false;
      }
      if (!formData.suenoNREM || !formData.suenoNREM.trim()) {
        showToast('Sueño NREM es obligatorio', 'error');
        return false;
      }
      if (!formData.parasomnias || !formData.parasomnias.trim()) {
        showToast('Parasomnias es obligatorio', 'error');
        return false;
      }
      if (!formData.asociadoFragmentosueno || !formData.asociadoFragmentosueno.trim()) {
        showToast('Asociado fragmento sueño es obligatorio', 'error');
        return false;
      }
      if (!formData.asociadoFragmentosuenoAumento || !formData.asociadoFragmentosuenoAumento.trim()) {
        showToast('Asociado fragmento sueño (aumento) es obligatorio', 'error');
        return false;
      }
      if (!formData.asociadosExtrasistolesVentricularesAumento || !formData.asociadosExtrasistolesVentricularesAumento.trim()) {
        showToast('Asociado extrasístoles ventriculares (aumento) es obligatorio', 'error');
        return false;
      }
    }
    
    if (formData.tipoMaquina === 'P' && (!formData.tipoModelo || !formData.tipoModelo.trim())) {
      showToast('Modelo es obligatorio para PR', 'error');
      return false;
    }
    
    // Campos siempre obligatorios independiente del tipo
    if (!formData.oximetriaDigital || !formData.oximetriaDigital.trim()) {
      showToast('Oximetría Digital es obligatorio', 'error');
      return false;
    }
    if (!formData.eventosRespiratorios || !formData.eventosRespiratorios.trim()) {
      showToast('Eventos respiratorios es obligatorio', 'error');
      return false;
    }
    if (!formData.asociadoExtrasistoleVentriculares || !formData.asociadoExtrasistoleVentriculares.trim()) {
      showToast('Aumento esfuerzo ventilatorio es obligatorio', 'error');
      return false;
    }
    if (!formData.actividadEKG || !formData.actividadEKG.trim()) {
      showToast('Actividad EKG es obligatorio', 'error');
      return false;
    }
    if (!formData.posicion || !formData.posicion.trim()) {
      showToast('Posición es obligatorio', 'error');
      return false;
    }
    if (!formData.ronquidos || !formData.ronquidos.trim()) {
      showToast('Ronquidos es obligatorio', 'error');
      return false;
    }
    if (!formData.sindromeApneasHipopneas || !formData.sindromeApneasHipopneas.trim()) {
      showToast('Síndrome apneas e hipopneas es obligatorio', 'error');
      return false;
    }
    
    // Campos obligatorios para PSG AKONIC
    if (formData.tipoMaquina === 'A' && (!formData.movimientosPiernas || !formData.movimientosPiernas.trim())) {
      showToast('Movimientos periódicos piernas es obligatorio', 'error');
      return false;
    }
    
    // Campos obligatorios para CPAP
    if (formData.tipoInforme === 'C') {
      if (formData.tipoMaquina === 'A' && (!formData.reboteSuenoREM || !formData.reboteSuenoREM.trim())) {
        showToast('Rebote de sueño REM es obligatorio para PSG AKONIC + CPAP', 'error');
        return false;
      }
      if (!formData.equipoUtilizado || !formData.equipoUtilizado.trim()) {
        showToast('Equipo utilizado es obligatorio para CPAP', 'error');
        return false;
      }
      if (!formData.tipoMascara || !formData.tipoMascara.trim()) {
        showToast('Tipo de máscara es obligatorio para CPAP', 'error');
        return false;
      }
      if (!formData.talleEquipo || !formData.talleEquipo.trim()) {
        showToast('Talle es obligatorio para CPAP', 'error');
        return false;
      }
      if (!formData.tolerancia || !formData.tolerancia.trim()) {
        showToast('Tolerancia subjetiva es obligatorio para CPAP', 'error');
        return false;
      }
      if (!formData.efectividad || !formData.efectividad.trim()) {
        showToast('Efectividad objetiva es obligatorio para CPAP', 'error');
        return false;
      }
    }
    
    return true;
  }, [formData]);

  const handleOximetryChange = useCallback((e) => {
    const { name, value } = e.target;
    let numericValue = value.replace(/[^0-9]/g, '');
    
    if (numericValue.length > 5) {
      numericValue = numericValue.slice(0, 5);
    }
    
    let formattedValue = '';
    if (numericValue.length > 0) {
      if (numericValue.length <= 2) {
        formattedValue = numericValue;
      } else {
        const integerPart = numericValue.slice(0, -2);
        const decimalPart = numericValue.slice(-2);
        formattedValue = integerPart + '.' + decimalPart;
      }
      
      const numValue = parseFloat(formattedValue);
      if (numValue <= 100) {
        setFormData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Handle oximetriaDigital changes
      if (name === 'oximetriaDigital') {
        if (value === '-') {
          newData.basal = '0';
          newData.maxima = '0';
          newData.minima = '0';
          newData.promedio = '0';
        } else {
          newData.basal = '';
          newData.maxima = '';
          newData.minima = '';
          newData.promedio = '';
        }
      }
      
      // Handle eventosRespiratorios changes
      if (name === 'eventosRespiratorios' && (value === 'N' || value === '-')) {
        newData.apneasObstructivas = '0';
        newData.apneasMixtas = '0';
        newData.apneasCentrales = '0';
        newData.hipopneas = '0';
        newData.totalApneasHipopneas = '0';
        newData.indiceApneas = '0';
        newData.indiceDisturbioRespiratorioGlobal = '0';
      }
      
      // Handle asociadoExtrasistoleVentriculares changes
      if (name === 'asociadoExtrasistoleVentriculares' && (value === 'N' || value === '-')) {
        newData.aumentoEsfuerzoVentilatorio = '0';
        newData.indiceEsfuerzoVentilatorio = '0';
        newData.indiceDisturbioRespiratorioGlobal = newData.indiceApneas;
      }
      
      // Handle movimientosPiernas changes
      if (name === 'movimientosPiernas' && (value === 'N' || value === '-')) {
        newData.txtMovimientosPiernas = '0';
        newData.indiceMovimientosPiernas = '0';
      }
      
      // Handle tipoInforme changes - clear CPAP specific fields when not CPAP
      if (name === 'tipoInforme' && value !== 'C') {
        newData.reboteSuenoREM = '';
        newData.horarioInicio = '';
        newData.equipoUtilizado = '';
        newData.tipoMascara = '';
        newData.talleEquipo = '';
        newData.presionInicial = '4';
        newData.presionMaxima = '';
        newData.presionRegistro = '';
        newData.presionTerapeutica = '';
        newData.presionPromedio = '';
        newData.tolerancia = '';
        newData.efectividad = '';
        newData.horasEfectivas = '';
      }
      
      // Handle tipoMaquina changes - clear machine specific fields
      if (name === 'tipoMaquina') {
        if (value === 'P') {
          // Clear PSG AKONIC specific fields
          newData.canales = '4';
          newData.microestructuraSueno = '';
          newData.suenoLentoN3 = '';
          newData.latenciaInicioSuenoREM = '0';
          newData.cicloSueno = '0';
          newData.suenoNREM = '';
          newData.txtNrem16 = '';
          newData.txtRem16 = '';
          newData.asociadoFragmentosuenoAumento = '';
          newData.asociadosExtrasistolesVentricularesAumento = '';
          newData.movimientosPiernas = '';
          newData.txtMovimientosPiernas = '0';
          newData.indiceMovimientosPiernas = '0';
          newData.parasomnias = '';
          newData.asociadoFragmentosueno = '';
          newData.asociadoExtrasistoleVentriculares = '';
        } else if (value === 'A') {
          // Clear PR specific fields
          newData.idoBMC = '';
        }
        // Reset practice data saved flag when machine type changes
        setReportGenerated(false);
      }
      
      // Handle canales changes - clear 16 channel specific fields when not 16
      if (name === 'canales' && value !== '16') {
        newData.txtNrem16 = '';
        newData.txtRem16 = '';
      }
      
      // Handle actividadEKG changes - set asociado extrasistoles based on selection
      if (name === 'actividadEKG') {
        if (value === 'E' || value === 'EB') {
          newData.asociadosExtrasistolesVentriculares = 'S';
          newData.asociadosExtrasistolesVentricularesAumento = 'S';
        } else {
          newData.asociadosExtrasistolesVentriculares = 'N';
          newData.asociadosExtrasistolesVentricularesAumento = 'N';
        }
      }
      
      // Calculate totals when apnea fields or sleep time change
      if (['apneasCentrales', 'apneasMixtas', 'apneasObstructivas', 'hipopneas', 'tiempoTotalSueno', 'aumentoEsfuerzoVentilatorio', 'asociadoExtrasistoleVentriculares'].includes(name)) {
        const calculations = calculateApneasHipopneas(newData);
        Object.assign(newData, calculations);
      }
      
      // Calculate leg movements index
      if (['txtMovimientosPiernas', 'tiempoTotalSueno', 'movimientosPiernas'].includes(name)) {
        const legCalculations = calculateMovimientosPiernas(newData);
        Object.assign(newData, legCalculations);
      }
      
      return newData;
    });
  }, [calculateApneasHipopneas, calculateMovimientosPiernas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updateData = {
        idEstadoTurno: parseInt(formData.idEstadoTurno) || null,
        idProcesador: parseInt(formData.procesador) || null,
        subEstado: String(formData.idEstadoTurno) === '19' ? formData.subEstado : null
      };
      
      await turnosAPI.updateEstadoYProcesadorDatosPracticas(turno.idTurno, updateData);
      showToast('Estado y procesador actualizados correctamente', 'success');
      setTimeout(() => {
        window.scrollTo(0, 0);
        if (onGuardar) {
          onGuardar();
        } else {
          onVolver();
        }
      }, 1500);
    } catch (err) {
      setError('Error al actualizar el turno');
      showToast('Error al actualizar el turno', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const normalizeText = (text) => {
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedExtensions = ['.docx', '.doc', '.pdf'];
    
    const validFiles = files.filter(file => {
      const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return allowedExtensions.includes(extension);
    });

    if (validFiles.length !== files.length) {
      showToast('Solo se permiten archivos DOC, DOCX y PDF', 'warning');
      e.target.value = '';
      return;
    }

    // Validar que el nombre del archivo contenga apellido y nombre
    const archivosValidos = validFiles.filter(file => {
      const nombreArchivo = normalizeText(file.name);
      const apellidoNorm = normalizeText(formData.apellido);
      const nombreNorm = normalizeText(formData.nombre);
      
      return nombreArchivo.includes(apellidoNorm) && nombreArchivo.includes(nombreNorm);
    });

    if (archivosValidos.length !== validFiles.length) {
      showToast(`Los archivos deben contener el apellido "${formData.apellido}" y nombre "${formData.nombre}" del paciente`, 'warning');
      e.target.value = ''; 
      return; 
    } 

    setSelectedFiles(archivosValidos);
    if (archivosValidos.length > 0) {
      showToast(`${archivosValidos.length} archivo(s) seleccionado(s)`, 'info');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleUploadFiles = async (isInforme = false) => {
    console.log('[Upload] Iniciando subida. isInforme:', isInforme, 'loading:', loading, 'selectedFiles:', selectedFiles.length);
    if (selectedFiles.length === 0) {
      showToast('Seleccione archivos para subir', 'warning');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    setLoading(true);
    try {
      // Leer todos los archivos a ArrayBuffer antes de subir para evitar ERR_UPLOAD_FILE_CHANGED
      const fileBuffers = await Promise.all(
        selectedFiles.map(file =>
          file.arrayBuffer().then(buffer => ({ buffer, name: file.name, type: file.type }))
        )
      );

      for (const { buffer, name, type } of fileBuffers) {
        console.log('[Upload] Subiendo archivo:', name, 'size:', buffer.byteLength);

        let fileName = name;
        if (isInforme) {
          const lastDotIndex = name.lastIndexOf('.');
          const nameWithoutExt = name.substring(0, lastDotIndex);
          const extension = name.substring(lastDotIndex);
          fileName = nameWithoutExt.endsWith('.INFORME')
            ? name
            : `${nameWithoutExt}.INFORME${extension}`;
        }

        const fileToUpload = new File([buffer], fileName, { type });
        await adjuntosAPI.uploadFile(fileToUpload, turno.idTurno, isInforme);
      }
      
      showToast(`${isInforme ? 'Informes' : 'Archivos'} subidos correctamente`, 'success');
      console.log('[Upload] Subida exitosa');
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (informeInputRef.current) informeInputRef.current.value = '';
      
      // Recargar adjuntos
      const adjuntosResponse = await adjuntosAPI.getAdjuntos(turno.idTurno, 'I');
      setAdjuntos(adjuntosResponse.data || []);
    } catch (err) {
      console.error('[Upload] Error al subir:', err?.message, 'status:', err?.response?.status, 'data:', err?.response?.data);
      showToast('Error al subir archivos', 'danger');
    } finally {
      console.log('[Upload] Finally - setLoading(false)');
      setLoading(false);
    }
  };

  const handleDeleteAdjunto = async () => {
    if (!adjuntoToDelete) return;
    
    setLoading(true);
    setShowDeleteModal(false);
    try {
      await adjuntosAPI.deleteAdjunto(adjuntoToDelete);
      showToast('Adjunto eliminado correctamente', 'success');
      
      // Recargar adjuntos
      const adjuntosResponse = await adjuntosAPI.getAdjuntos(turno.idTurno, 'I');
      setAdjuntos(adjuntosResponse.data || []);
    } catch (err) {
      showToast('Error al eliminar adjunto', 'danger');
    } finally {
      setLoading(false);
      setAdjuntoToDelete(null);
    }
  };

  const handleDescargarEeg = async (idAdjunto) => {
    setDescargandoEeg(idAdjunto);
    try {
      const response = await adjuntosAPI.descargarEeg(idAdjunto);
      const url = response.data?.url;
      if (url) {
        window.open(url, '_blank');
      } else {
        showToast('No se pudo obtener el link de descarga', 'danger');
      }
    } catch (err) {
      showToast('Error al descargar el archivo EEG', 'danger');
    } finally {
      setDescargandoEeg(null);
    }
  };

  const handleGuardarObservacion = async () => {
    if (!nuevaObservacion.trim()) {
      showToast('Ingrese una observación', 'warning');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    
    setLoading(true);
    try {
      const user = localStorage.getItem('user');
      let username = 'usuario_sistema';
      if (user && user !== 'undefined') {
        try {
          const userData = JSON.parse(user);
          username = userData.username || 'usuario_sistema';
        } catch (e) {
          console.error('Error parsing user:', e);
        }
      }
      
      await observacionesAPI.guardarObservacion({
        idTurno: turno.idTurno,
        observacion: nuevaObservacion,
        usr: username
      });
      
      showToast('Observación guardada correctamente', 'success');
      setNuevaObservacion('');
      
      // Recargar observaciones
      const observacionesResponse = await observacionesAPI.getObservaciones(turno.idTurno);
      setObservaciones(observacionesResponse.data || []);
    } catch (err) {
      showToast('Error al guardar observación', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      
      const reportType = formData.tipoMaquina === 'P' 
        ? (formData.tipoInforme === 'C' ? 3 : 2)
        : (formData.tipoInforme === 'C' ? 4 : 1);
      const response = await practicasAPI.generarInforme(turno.idTurno, reportType);
      
      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Informe_Turno_${turno.idTurno}.docx`;
        a.style.display = 'none';
        document.body.appendChild(a);
        
        requestAnimationFrame(() => {
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        });
        
        showToast('Informe descargado correctamente', 'success');
      }
    } catch (err) {
      showToast('Error al descargar el informe', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePracticeData = async () => {
    // Validar campos obligatorios
    if (!validateRequiredFields()) {
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    
    setLoading(true);
    try {
      const practicaDto = {
        IdTurno: turno.idTurno,
        TipoMaquina: formData.tipoMaquina,
        TipoInforme: formData.tipoInforme,
        TipoModelo: formData.tipoModelo,
        Canales: formData.canales !== '' && formData.canales != null ? parseInt(formData.canales) : null,
        MicroestructuraSeno: formData.microestructuraSueno,
        TimepoTotalSueno: parseFloat(formData.tiempoTotalSueno) || null,
        SuenoLentoN3: formData.suenoLentoN3,
        LatenciaRem: parseFloat(formData.latenciaInicioSuenoREM) || null,
        CiclosSueno: parseFloat(formData.cicloSueno) || null,
        SuenoNrem: formData.suenoNREM,
        TextoNrem16: formData.txtNrem16,
        TextoRem16: formData.txtRem16,
        OximetriaBasal: parseFloat(formData.basal) || null,
        OximetriaMaxima: parseFloat(formData.maxima) || null,
        OximetriaMinima: parseFloat(formData.minima) || null,
        OximetriaPromedio: parseFloat(formData.promedio) || null,
        ReboteSuenoRem: formData.reboteSuenoREM,
        HoraInicio: formData.horarioInicio ? new Date(`1970-01-01T${formData.horarioInicio}:00`) : null,
        IdEquipoCpap: parseInt(formData.equipoUtilizado) || null,
        TipoMascara: formData.tipoMascara,
        Talle: formData.talleEquipo,
        PresionInicial: parseFloat(formData.presionInicial) || null,
        PresionMaxima: parseFloat(formData.presionMaxima) || null,
        PresionRegistro: parseFloat(formData.presionRegistro) || null,
        PresionTerapeutica: parseFloat(formData.presionTerapeutica) || null,
        PresionPromedio: parseFloat(formData.presionPromedio) || null,
        Tolerancia: formData.tolerancia,
        Efectividad: formData.efectividad,
        TotalHorasEfectivas: formData.horasEfectivas ? new Date(`1970-01-01T${formData.horasEfectivas}:00.000Z`) : null,
        TiempoTotalRegistro: 0,
        ApneasObstruidas: parseFloat(formData.apneasObstructivas) || null,
        ApneasMixtas: parseFloat(formData.apneasMixtas) || null,
        ApneasCentrales: parseFloat(formData.apneasCentrales) || null,
        Hipopneas: parseFloat(formData.hipopneas) || null,
        AumentoEsfuerzoVentilatorio: parseFloat(formData.aumentoEsfuerzoVentilatorio) || 0,
        AsociadoFragmentoSuenoAumento: formData.asociadoFragmentosuenoAumento,
        AsociadoExtrasistoleVentricularesAumento: formData.asociadoFragmentosuenoAumento,
        MovimientoPeriodicoPiernas: parseFloat(formData.txtMovimientosPiernas) || null,
        ActividadEkg: formData.actividadEKG,
        Posicion: formData.posicion,
        Ronquidos: formData.ronquidos,
        Parasomnias: formData.parasomnias,
        AsociadoFragmentoSueno: formData.asociadoFragmentosueno,
        SindromeApneasHipopneas: formData.sindromeApneasHipopneas,
        asociadoExtrasistoleVentriculares: formData.asociadoExtrasistoleVentriculares,
        Ido: formData.idoBMC,
        Idr: formData.indiceDisturbioRespiratorioGlobal,
        Iah: formData.indiceApneas,
        Iav: formData.indiceEsfuerzoVentilatorio,
        Usr: (() => {
          const user = localStorage.getItem('user');
          if (user && user !== 'undefined') {
            try {
              const userData = JSON.parse(user);
              return userData.username || 'usuario_sistema';
            } catch (e) {
              return 'usuario_sistema';
            }
          }
          return 'usuario_sistema';
        })()
      };
      
      await practicasAPI.guardarPractica(practicaDto);
      showToast('Datos de la práctica guardados correctamente', 'success');
      
      // Generate preview after saving
      try {
        const reportType = formData.tipoMaquina === 'P' 
          ? (formData.tipoInforme === 'C' ? 3 : 2)
          : (formData.tipoInforme === 'C' ? 4 : 1);
        const reportResponse = await practicasAPI.generarInforme(turno.idTurno, reportType);
        
        if (reportResponse.data) {
          const docxBlob = new Blob([reportResponse.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const pdfResponse = await practicasAPI.convertirDocxAPdf(docxBlob, `Informe_${turno.idTurno}.docx`);
          
          if (pdfResponse.data) {
            const pdfUrl = URL.createObjectURL(pdfResponse.data);
            setPreviewUrl(pdfUrl);
            setReportGenerated(true);
          }
        }
      } catch (reportError) {
        console.warn('Could not generate report preview:', reportError);
      }
    } catch (err) {
      showToast('Error al guardar los datos de la práctica', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const edad = useMemo(() => calcularEdad(formData.fechaNacimiento), [formData.fechaNacimiento]);

  return (
    <>
      <ToastContainer toast={toast} onClose={() => setToast(null)} />
      <DocumentViewer
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
        url={selectedDoc?.url}
        title={selectedDoc?.name}
      />
      <div className="editar-practica">
        <Header />
        <div className="editar-practica-header">
          <div className="editar-practica-header-content">
            <div className="title-section">
              <h1 className="page-title">Turno #{turno?.idTurno}</h1>
              <span className="subtitle">Gestión de práctica médica</span>
            </div>
          </div>
        </div>

      {error && <div className="alert-error">{error}</div>}

      <Form onSubmit={handleSubmit} className="form-container">
        {/* Datos del Paciente */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Datos del paciente</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12} xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Apellido</Form.Label>
                  <Form.Control
                    type="text"
                    name="apellido"
                    value={formData.apellido || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
                 <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Nacimiento</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechaNacimiento"
                    value={formData.fechaNacimiento || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
                <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo Doc.</Form.Label>
                  <Form.Control
                    type="text"
                    name="tipoDni"
                    value={formData.tipoDni || 'DNI'}
                    readOnly
                  />
                </Form.Group>
              </Col>

                   <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Documento</Form.Label>
                  <Form.Control
                    type="text"
                    name="dni"
                    value={formData.dni || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Edad</Form.Label>
                  <Form.Control
                    type="text"
                    value={edad || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Obra Social / Prepaga</Form.Label>
                  <Form.Control
                    type="text"
                    name="obraSocial"
                    value={formData.obraSocial || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Plan</Form.Label>
                  <Form.Control
                    type="text"
                    name="plan"
                    value={formData.plan || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Celular</Form.Label>
                  <Form.Control
                    type="text"
                    name="celular"
                    value={formData.celular || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Correo</Form.Label>
                  <Form.Control
                    type="email"
                    name="correo"
                    value={formData.correo || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Dirección</Form.Label>
                  <Form.Control
                    type="text"
                    name="direccion"
                    value={formData.direccion || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Localidad</Form.Label>
                  <Form.Control
                    type="text"
                    name="localidad"
                    value={formData.localidad || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Código Postal</Form.Label>
                  <Form.Control
                    type="text"
                    name="codigoPostal"
                    value={formData.codigoPostal || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Vencimiento Autorización</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechaVenAutorizacion"
                    value={formData.fechaVenAutorizacion || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Peso</Form.Label>
                  <Form.Control
                    type="text"
                    name="peso"
                    value={formData.peso || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Talle</Form.Label>
                  <Form.Control
                    type="text"
                    name="talle"
                    value={formData.talle || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        {/* Datos del Turno */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Datos del Turno</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Inicio</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="fechaInicio"
                    value={formData.fechaInicio || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Fin</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="fechaFin"
                    value={formData.fechaFin || ''}
                    onChange={handleInputChange}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Entrega</Form.Label>
                  <Form.Control
                    type="date"
                    name="fechaEntrega"
                    value={formData.fechaEntrega || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Estudio</Form.Label>
                  <Form.Control
                    type="text"
                    name="tipoEstudio"
                    value={formData.tipoEstudio || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Centro</Form.Label>
                  <Form.Control
                    type="text"
                    name="centro"
                    value={formData.centro || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Como enviamos los estudios</Form.Label>
                  <Form.Control
                    type="text"
                    name="envioEstudios"
                    value={(() => {
                      switch(formData.envioEstudios) {
                        case 'E': return 'Correo electrónico';
                        case 'P': return 'Correo postal';
                        case 'W': return 'WhatsApp';
                        case 'C': return 'Personalmente';
                        default: return formData.envioEstudios || '';
                      }
                    })()}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Diagnóstico</Form.Label>
                  <Form.Control
                    type="text"
                    name="diagnostico"
                    value={formData.diagnostico || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Técnico</Form.Label>
                  <Form.Control
                    type="text"
                    name="tecnico"
                     value={formData.medico || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
      
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Turno generado</Form.Label>
                  <Form.Control
                    type="text"
                    name="turnoGenerado"
                    value={formData.turnoGenerado || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
                      <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Documentación presentada por el paciente</Form.Label>
                  <div className="requisitos-list">
                    {formData.requisitos && formData.requisitos.length > 0 ? (
                      formData.requisitos.map((req, index) => (
                        <div key={index} className="form-check">
                          <input className="form-check-input" type="checkbox" checked={req.checked} disabled />
                          <label className="form-check-label">{req.nombre}</label>
                        </div>
                      ))
                    ) : (
                      adjuntosTecnicos.length > 0 ? (
                        adjuntosTecnicos.map((adjunto, index) => (
                          <div key={index} className="adjunto-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                            <Button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedDoc({ url: adjuntosAPI.getPublicUrl(adjunto.nombre), name: adjunto.nombre });
                                setShowDocViewer(true);
                              }}
                              className="text-decoration-none btn btn-link p-0"
                              style={{ textAlign: 'left' }}
                            >
                              {adjunto.nombre}
                            </Button>
                            <Badge bg="info">Documento <br></br>Admnistrativo</Badge>
                          </div>
                        )) 
                      ) : (
                        <span className="text-muted">No hay documentación registrada</span>
                      )
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
          </div>
        </div>

        {/* Estados del Turno */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Estados del Turno</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12}>
                {estadosTurnoLog.length > 0 && (
                  <div className="observaciones-container">
                    <h6 className="observaciones-title">Historial de Estados</h6>
                    <div className="table-container-obs">
                      <DataTable className="data-table-obs">
                        <thead>
                          <tr>
                            <th>Estado Turno</th>
                            <th>Usuario Alta</th>
                            <th>Usuario Modificación</th>
                          </tr>
                        </thead>
                        <tbody> 
                          {estadosTurnoLog.map((estado, index) => (
                            <tr key={index}>
                              <td>{estado.estadoTurno || 'N/A'}</td>
                              <td>{estado.usrAlta || 'N/A'}</td>
                              <td>{estado.usrModif || 'N/A'}</td> 
                            </tr>
                          ))} 
                        </tbody>  
                      </DataTable>
                    </div> 
                  </div>
                )} 
              </Col> 
            </Row> 
          </div>
        </div>

        {/* Especialista Derivador */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Especialista Derivador</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    type="text"
                    value={especialistaDerivador?.nombre || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Especialidad</Form.Label>
                  <Form.Control
                    type="text"
                    value={especialistaDerivador?.especialidad || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Localidad</Form.Label>
                  <Form.Control
                    type="text"
                    value={especialistaDerivador?.localidad || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        {/* Observaciones */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Observaciones</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observaciones del Turno</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={formData.observaciones || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        {/* Informe Técnico */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Informe Tecnico</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado del Estudio</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      name="estadoEstudio"
                      value="S"
                      label="Realizado"
                      checked={formData.estadoEstudio === 'S'}
                      onChange={handleInputChange}
                      disabled
                    />
                    <Form.Check
                      inline
                      type="radio"
                      name="estadoEstudio"
                      value="N"
                      label="No Completado"
                      checked={formData.estadoEstudio === 'N'}
                      onChange={handleInputChange}
                      disabled
                    />
                    <Form.Check
                      inline
                      type="radio"
                      name="estadoEstudio"
                      value="C"
                      label="Cancelado"
                      checked={formData.estadoEstudio === 'C'}
                      onChange={handleInputChange}
                      disabled
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Equipo</Form.Label>
                  <Form.Control
                    type="text"
                    name="equipo"
                    value={formData.equipo || ''}
                    readOnly  
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Equipo de CPAP</Form.Label>
                  <Form.Control
                    type="text"
                    name="equipoCPAP"
                    value={formData.equipoCPAP || ''}
                    readOnly 
                  />
                </Form.Group>
              </Col>
            </Row> 
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Motivo / Observación</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="motivoObservacion"
                    value={formData.motivoObservacion || ''}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        {/* Datos de la Práctica */}
        {(!isMedico || reportGenerated) && (
          <div className="section-card practice-section">
            <div className="section-header">
              <h3 className="section-title">Datos de la practica</h3>
            </div>
            <div className="section-content">
              {!reportGenerated ? (
                (user?.procesador === 'S' || user?.procesador === true) ? (
                <>
                <div className="config-grid">
                  <div className="config-group">
                    <label className="config-label">Tipo de Equipo *</label>
                    <div className="radio-group">
                      <label className={`radio-option ${formData.tipoMaquina === 'A' ? 'selected' : ''}`}>
                        <input type="radio" name="tipoMaquina" value="A" checked={formData.tipoMaquina === 'A'} onChange={handleInputChange} />
                        <span>PSG AKONIC</span>
                      </label>
                      <label className={`radio-option ${formData.tipoMaquina === 'P' ? 'selected' : ''}`}>
                        <input type="radio" name="tipoMaquina" value="P" checked={formData.tipoMaquina === 'P'} onChange={handleInputChange} />
                        <span>PR</span>
                      </label>
                    </div>
                  </div>
                  {formData.tipoMaquina === 'P' && (
                    <div className="config-group">
                      <label className="config-label">Modelo *</label>
                      <div className="radio-group">
                        <label className={`radio-option ${formData.tipoModelo === 'L' ? 'selected' : ''}`}>
                          <input type="radio" name="tipoModelo" value="L" checked={formData.tipoModelo === 'L'} onChange={handleInputChange} />
                          <span>Apnea Link</span>
                        </label>
                        <label className={`radio-option ${formData.tipoModelo === 'B' ? 'selected' : ''}`}>
                          <input type="radio" name="tipoModelo" value="B" checked={formData.tipoModelo === 'B'} onChange={handleInputChange} />
                          <span>BMC</span>
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="config-group">
                    <label className="config-label">Tipo de Informe *</label>
                    <div className="radio-group">
                      <label className={`radio-option ${formData.tipoInforme === 'B' ? 'selected' : ''}`}>
                        <input type="radio" name="tipoInforme" value="B" checked={formData.tipoInforme === 'B'} onChange={handleInputChange} />
                        <span>Basal</span>
                      </label>
                      <label className={`radio-option ${formData.tipoInforme === 'C' ? 'selected' : ''}`}>
                        <input type="radio" name="tipoInforme" value="C" checked={formData.tipoInforme === 'C'} onChange={handleInputChange} />
                        <span>CPAP</span>
                      </label>
                    </div>
                  </div>
                </div>
                

                
                {formData.tipoMaquina === 'A' && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>* Canales</Form.Label>
                        <div>
                          <Form.Check
                            inline
                            type="radio"
                            name="canales"
                            value="4"
                            label="4"
                            checked={formData.canales === '4'}
                            onChange={handleInputChange}
                          />
                          <Form.Check
                            inline
                            type="radio"
                            name="canales"
                            value="16"
                            label="16"
                            checked={formData.canales === '16'}
                            onChange={handleInputChange}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {formData.tipoMaquina === 'A' && (
                      <>
                        <Row><Col><h6><em>Estructura del Sueño</em></h6></Col></Row>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>* Microestructura del Sueño</Form.Label>
                              <Form.Select name="microestructuraSueno" value={formData.microestructuraSueno} onChange={handleInputChange}>
                                <option value="">Seleccione...</option>
                                <option value="A">Adecuada</option>
                                <option value="L">Alterada</option>
                                <option value="D">Dentro de límites normales</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>* Sueño lento N3</Form.Label>
                              <Form.Select name="suenoLentoN3" value={formData.suenoLentoN3} onChange={handleInputChange}>
                                <option value="">Seleccione...</option>
                                <option value="S">Sí</option>
                                <option value="N">No</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Latencia inicio sueño REM (min.)</Form.Label>
                              <Form.Control type="number" name="latenciaInicioSuenoREM" value={formData.latenciaInicioSuenoREM} onChange={handleInputChange} />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Ciclos de Sueño</Form.Label>
                              <Form.Control type="number" name="cicloSueno" value={formData.cicloSueno} onChange={handleInputChange} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>{formData.tipoMaquina === 'A' ? '* ' : ''}Sueño NREM</Form.Label>
                              <Form.Select name="suenoNREM" value={formData.suenoNREM} onChange={handleInputChange}>
                                <option value="">Seleccione...</option>
                                <option value="S">Sí</option>
                                <option value="N">No</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        {formData.canales === '16' && (
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Sueño NREM:</Form.Label>
                                <Form.Control as="textarea" rows={5} name="txtNrem16" value={formData.txtNrem16} onChange={handleInputChange} />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Sueño REM:</Form.Label>
                                <Form.Control as="textarea" rows={5} name="txtRem16" value={formData.txtRem16} onChange={handleInputChange} />
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tiempo Total de Sueño (min.)</Form.Label>
                          <Form.Control type="number" name="tiempoTotalSueno" value={formData.tiempoTotalSueno} onChange={handleInputChange} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Oximetría Digital</Form.Label>
                          <Form.Select name="oximetriaDigital" value={formData.oximetriaDigital} onChange={handleInputChange}>
                            <option value="">Seleccione...</option>
                            <option value="S">Sí</option>
                            <option value="-">No se evalúa</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.oximetriaDigital === 'S' ? '* ' : ''}Basal %</Form.Label>
                          <Form.Control type="text" name="basal" value={formData.basal} onChange={handleOximetryChange} disabled={formData.oximetriaDigital !== 'S'} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.oximetriaDigital === 'S' ? '* ' : ''}Máxima %</Form.Label>
                          <Form.Control type="text" name="maxima" value={formData.maxima} onChange={handleOximetryChange} disabled={formData.oximetriaDigital !== 'S'} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.oximetriaDigital === 'S' ? '* ' : ''}Mínima %</Form.Label>
                          <Form.Control type="text" name="minima" value={formData.minima} onChange={handleOximetryChange} disabled={formData.oximetriaDigital !== 'S'} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.oximetriaDigital === 'S' ? '* ' : ''}Promedio %</Form.Label>
                          <Form.Control type="text" name="promedio" value={formData.promedio} onChange={handleOximetryChange} disabled={formData.oximetriaDigital !== 'S'} />
                        </Form.Group>
                      </Col>
                    </Row>
                    {formData.tipoMaquina === 'P' && (
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>IDO</Form.Label>
                            <Form.Control type="text" name="idoBMC" value={formData.idoBMC} onChange={handleInputChange} />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {formData.canales === '4' && (
                      <>
                        {formData.tipoMaquina === 'A' && formData.tipoInforme === 'C' && (
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>* Rebote de sueño REM</Form.Label>
                                <div>
                                  <Form.Check inline type="radio" name="reboteSuenoREM" value="S" label="Sí" checked={formData.reboteSuenoREM === 'S'} onChange={handleInputChange} />
                                  <Form.Check inline type="radio" name="reboteSuenoREM" value="N" label="No" checked={formData.reboteSuenoREM === 'N'} onChange={handleInputChange} />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                        {formData.tipoInforme === 'C' && (
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Horario de Inicio</Form.Label>
                                <Form.Control type="time" name="horarioInicio" value={formData.horarioInicio} onChange={handleInputChange} />
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                        {formData.tipoInforme === 'C' && (
                          <>
                            <Row>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>{formData.tipoInforme === 'C' ? '* ' : ''}Equipo utilizado</Form.Label>
                                  <Form.Select
                                    name="equipoUtilizado"
                                    value={formData.equipoUtilizado}
                                    onChange={handleInputChange}
                                  >
                                    <option value="-1">Seleccione...</option>
                                    {equipos.map((equipo) => (
                                      <option key={equipo.idEquipo} value={equipo.idEquipo}>
                                        {equipo.equipo || equipo.equipo1}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>{formData.tipoInforme === 'C' ? '* ' : ''}Tipo de máscara</Form.Label>
                                  <div>
                                    <Form.Check inline type="radio" name="tipoMascara" value="N" label="Nasal" checked={formData.tipoMascara === 'N'} onChange={handleInputChange} />
                                    <Form.Check inline type="radio" name="tipoMascara" value="B" label="BucoNasal" checked={formData.tipoMascara === 'B'} onChange={handleInputChange} />
                                  </div>
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>{formData.tipoInforme === 'C' ? '* ' : ''}Talle</Form.Label>
                                  <Form.Select name="talleEquipo" value={formData.talleEquipo} onChange={handleInputChange}>
                                    <option value="">Seleccione...</option>
                                    <option value="S">Small</option>
                                    <option value="M">Medium</option>
                                    <option value="L">Large</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Presión Inicial (cm H2O)</Form.Label>
                                  <Form.Control type="number" name="presionInicial" value={formData.presionInicial} onChange={handleInputChange} step="0.1" />
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Presión Máxima (cm H2O)</Form.Label>
                                  <Form.Control type="number" name="presionMaxima" value={formData.presionMaxima} onChange={handleInputChange} step="0.1" />
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Presión 95% registro (cm H2O)</Form.Label>
                                  <Form.Control type="number" name="presionRegistro" value={formData.presionRegistro} onChange={handleInputChange} step="0.1" />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Presión terapéutica (cm H2O)</Form.Label>
                                  <Form.Control type="number" name="presionTerapeutica" value={formData.presionTerapeutica} onChange={handleInputChange} step="0.1" />
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Presión promedio (cm H2O)</Form.Label>
                                  <Form.Control type="number" name="presionPromedio" value={formData.presionPromedio} onChange={handleInputChange} step="0.1" />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>{formData.tipoInforme === 'C' ? '* ' : ''}Tolerancia subjetiva</Form.Label>
                                  <Form.Select name="tolerancia" value={formData.tolerancia} onChange={handleInputChange}>
                                    <option value="">Seleccione...</option>
                                    <option value="B">Buena</option>
                                    <option value="R">Regular</option>
                                    <option value="M">Mala</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>{formData.tipoInforme === 'C' ? '* ' : ''}Efectividad objetiva</Form.Label>
                                  <Form.Select name="efectividad" value={formData.efectividad} onChange={handleInputChange}>
                                    <option value="">Seleccione...</option>
                                    <option value="N">No efectivo</option>
                                    <option value="P">Poco efectivo</option>
                                    <option value="E">Efectivo</option>
                                    <option value="M">Muy efectivo</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={12}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Total de horas efectivas con la máscara colocada</Form.Label>
                                  <Form.Control type="time" name="horasEfectivas" value={formData.horasEfectivas} onChange={handleInputChange} />
                                </Form.Group>
                              </Col>
                            </Row>
                          </>
                        )}
                      </>
                    )}

                    <Row><Col><h6><em>Eventos respiratorios</em></h6></Col></Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Eventos respiratorios</Form.Label>
                          <Form.Select name="eventosRespiratorios" value={formData.eventosRespiratorios} onChange={handleInputChange}>
                            <option value="">Seleccione...</option>
                            <option value="S">Sí</option>
                            <option value="N">No</option>
                            <option value="-">No se evalúa</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.eventosRespiratorios === 'S' ? '* ' : ''}Apneas obstructivas</Form.Label>
                          <Form.Control type="number" name="apneasObstructivas" value={formData.apneasObstructivas} onChange={handleInputChange} disabled={formData.eventosRespiratorios === 'N' || formData.eventosRespiratorios === '-'} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.eventosRespiratorios === 'S' ? '* ' : ''}Apneas mixtas</Form.Label>
                          <Form.Control type="number" name="apneasMixtas" value={formData.apneasMixtas} onChange={handleInputChange} disabled={formData.eventosRespiratorios === 'N' || formData.eventosRespiratorios === '-'} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.eventosRespiratorios === 'S' ? '* ' : ''}Apneas centrales</Form.Label>
                          <Form.Control type="number" name="apneasCentrales" value={formData.apneasCentrales} onChange={handleInputChange} disabled={formData.eventosRespiratorios === 'N' || formData.eventosRespiratorios === '-'} />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.eventosRespiratorios === 'S' ? '* ' : ''}Hipopneas</Form.Label>
                          <Form.Control type="number" name="hipopneas" value={formData.hipopneas} onChange={handleInputChange} disabled={formData.eventosRespiratorios === 'N' || formData.eventosRespiratorios === '-'} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Total apneas e hipopneas</Form.Label>
                          <Form.Control type="number" name="totalApneasHipopneas" value={formData.totalApneasHipopneas} readOnly />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Índice apneas/hora sueño</Form.Label>
                          <Form.Control type="number" name="indiceApneas" value={formData.indiceApneas} readOnly />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Aumento esfuerzo ventilatorio</Form.Label>
                          <Form.Select name="asociadoExtrasistoleVentriculares" value={formData.asociadoExtrasistoleVentriculares} onChange={handleInputChange}>
                            <option value="">Seleccione...</option>
                            <option value="S">Sí</option>
                            <option value="N">No</option>
                            <option value="-">No se evalúa</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{formData.asociadoExtrasistoleVentriculares === 'S' ? '* ' : ''}Cantidad</Form.Label>
                          <Form.Control type="number" name="aumentoEsfuerzoVentilatorio" value={formData.aumentoEsfuerzoVentilatorio} onChange={handleInputChange} disabled={formData.asociadoExtrasistoleVentriculares !== 'S'} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Índice esfuerzo ventilatorio</Form.Label>
                          <Form.Control type="number" name="indiceEsfuerzoVentilatorio" value={formData.indiceEsfuerzoVentilatorio} readOnly />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Índice disturbio respiratorio global</Form.Label>
                          <Form.Control type="number" name="indiceDisturbioRespiratorioGlobal" value={formData.indiceDisturbioRespiratorioGlobal} readOnly />
                        </Form.Group>
                      </Col>
                    </Row>

                    {formData.tipoMaquina === 'A' && (
                      <>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>* Asociado fragmento sueño (aumento)</Form.Label>
                              <Form.Select name="asociadoFragmentosuenoAumento" value={formData.asociadoFragmentosuenoAumento} onChange={handleInputChange}>
                                <option value="">Seleccione...</option>
                                <option value="S">Sí</option>
                                <option value="N">No</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>* Movimientos periódicos piernas</Form.Label>
                              <Form.Select name="movimientosPiernas" value={formData.movimientosPiernas} onChange={handleInputChange}>
                                <option value="">Seleccione...</option>
                                <option value="S">Sí</option>
                                <option value="N">No</option>
                                <option value="-">No se evalúa</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>{formData.movimientosPiernas === 'S' ? '* ' : ''}Cantidad movimientos</Form.Label>
                              <Form.Control type="number" name="txtMovimientosPiernas" value={formData.txtMovimientosPiernas} onChange={handleInputChange} disabled={formData.movimientosPiernas === 'N' || formData.movimientosPiernas === '-'} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Índice movimientos piernas</Form.Label>
                              <Form.Control type="number" name="indiceMovimientosPiernas" value={formData.indiceMovimientosPiernas} onChange={handleInputChange} />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Actividad EKG</Form.Label>
                          <Form.Select name="actividadEKG" value={formData.actividadEKG} onChange={handleInputChange}>
                            <option value="">Seleccione...</option>
                            <option value="D">Dentro de límites normales</option>
                            <option value="A">Alterada</option>
                            <option value="B">BTC</option>
                            <option value="E">Extrasístoles ventriculares</option>
                            <option value="EB">Extrasístoles y BTC</option>
                            <option value="N">No se evalúa</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Posición</Form.Label>
                          <Form.Select name="posicion" value={formData.posicion} onChange={handleInputChange}>
                            <option value="V">Varios cambios de posición</option>
                            <option value="R">No se registran</option>
                            <option value="N">No se evalúa</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Ronquidos</Form.Label>
                          <Form.Select name="ronquidos" value={formData.ronquidos} onChange={handleInputChange}>
                            <option value="">Seleccione...</option>
                            <option value="R">No se registran</option>
                            <option value="L">Leve intensidad</option>
                            <option value="M">Moderada intensidad</option>
                            <option value="S">Severas</option>
                            <option value="U">Uniformes</option>
                            <option value="N">No se evalúa</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      {formData.tipoMaquina === 'A' && (
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>* Parasomnias</Form.Label>
                            <Form.Select name="parasomnias" value={formData.parasomnias} onChange={handleInputChange}>
                              <option value="">Seleccione...</option>
                              <option value="S">Sí</option>
                              <option value="N">No</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>* Síndrome apneas e hipopneas</Form.Label>
                          <Form.Control
                            type="text"
                            value={(() => {
                              switch(formData.sindromeApneasHipopneas) {
                                case 'L': return 'Leve';
                                case 'M': return 'Moderado';
                                case 'S': return 'Severo';
                                case 'N': return 'No';
                                default: return '';
                              }
                            })()}
                            readOnly
                          />
                          <Form.Text className="text-muted">
                            Calculado automáticamente según índice de apneas
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    {formData.tipoMaquina === 'A' && (
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>* Asociado fragmento sueño</Form.Label>
                            <Form.Select name="asociadoFragmentosueno" value={formData.asociadoFragmentosueno} onChange={handleInputChange}>
                              <option value="">Seleccione...</option>
                              <option value="S">Sí</option>
                              <option value="N">No</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                {(user?.procesador === 'S' || user?.procesador === true) && (
                  <Row>
                    <Col md={12}>
                      <Button
                        variant="success" 
                        onClick={handleSavePracticeData}
                        disabled={loading}
                        className="me-2"
                      >
                        <Save sx={{ fontSize: 18, mr: 0.5 }} />
                        {loading ? 'Guardando...' : 'Guardar Datos de Práctica'}
                      </Button>
                      <Button
                        variant="secondary" 
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const practiceResponse = await practicasAPI.obtenerPractica(turno.idTurno);
                            const practiceData = practiceResponse.data;
                            if (practiceData) {
                              const apneasCalculations = calculateApneasHipopneas({
                                apneasCentrales: practiceData.apneasCentrales?.toString() || '0',
                                apneasMixtas: practiceData.apneasMixtas?.toString() || '0',
                                apneasObstructivas: practiceData.apneasObstruidas?.toString() || '0',
                                hipopneas: practiceData.hipopneas?.toString() || '0',
                                tiempoTotalSueno: practiceData.timepoTotalSueno?.toString() || '0',
                                aumentoEsfuerzoVentilatorio: practiceData.aumentoEsfuerzoVentilatorio?.toString() || '0'
                              });
                              
                              setFormData(prev => ({
                                ...prev,
                                tipoMaquina: practiceData.tipoMaquina || 'A',
                                tipoModelo: practiceData.tipoModelo || 'L',
                                tipoInforme: practiceData.tipoInforme || 'B',
                                canales: practiceData.canales?.toString() || '4',
                                microestructuraSueno: practiceData.microestructuraSeno || '',
                                tiempoTotalSueno: practiceData.timepoTotalSueno?.toString() || '0',
                                apneasObstructivas: practiceData.apneasObstruidas?.toString() || '0',
                                apneasMixtas: practiceData.apneasMixtas?.toString() || '0',
                                apneasCentrales: practiceData.apneasCentrales?.toString() || '0',
                                hipopneas: practiceData.hipopneas?.toString() || '0',
                                ...apneasCalculations
                              }));
                              setReportGenerated(true);
                              showToast('Cambios cancelados', 'info');
                            }
                          } catch (error) {
                            showToast('Error al recargar datos', 'danger');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                      >
                        <Delete sx={{ fontSize: 18, mr: 0.5 }} />
                        Cancelar
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
                ) : (
                  <div className="alert alert-info">
                    <strong>Acceso restringido:</strong> Solo los usuarios procesadores pueden editar los datos de la práctica.
                  </div>
                )
            ) : (
              <>
                <Row className="mb-3">
                  <Col md={12}>
                    <h6>Previsualización del Informe</h6>
                    {(user?.procesador === 'S' || user?.procesador === true) ? (
                      <div style={{ width: '100%', height: '600px', border: '1px solid #ccc' }}>
                        {previewUrl ? (
                          <iframe
                            src={previewUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="Vista previa del informe PDF"
                          />
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <p>Cargando vista previa...</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      previewUrl ? (
                        <div style={{ width: '100%', height: '600px', border: '1px solid #ccc' }}>
                          <iframe
                            src={previewUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title="Vista previa del informe PDF"
                          />
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          <strong>Acceso restringido:</strong> No hay informe guardado disponible para visualizar.
                        </div>
                      )
                    )}
                  </Col>
                </Row>
                {(user?.procesador === 'S' || user?.procesador === true) ? (
                  <Row>
                    <Col md={12}>
                      <Button
                        variant="primary" 
                        onClick={async () => {
                        setLoading(true);
                        try {
                          const practiceResponse = await practicasAPI.obtenerPractica(turno.idTurno);
                          const practiceData = practiceResponse.data;
                          if (practiceData) {
                            // Calculate eventosRespiratorios based on API data
                            const apneasObstructivas = parseFloat(practiceData.apneasObstruidas) || 0;
                            const apneasMixtas = parseFloat(practiceData.apneasMixtas) || 0;
                            const apneasCentrales = parseFloat(practiceData.apneasCentrales) || 0;
                            const hipopneas = parseFloat(practiceData.hipopneas) || 0;
                            const eventosRespiratorios = (apneasObstructivas > 0 || apneasMixtas > 0 || apneasCentrales > 0 || hipopneas > 0) ? 'S' : 'N';
                            
                            setFormData(prev => ({
                              ...prev,
                              eventosRespiratorios
                            }));
                          }
                        } catch (error) {
                          console.warn('Error al cargar datos para edición:', error);
                        } finally {
                          setLoading(false);
                        }
                        setReportGenerated(false);
                      }}
                      className="me-2"
                        disabled={loading}
                      >
                        <Edit sx={{ fontSize: 18, mr: 0.5 }} />
                        Editar
                      </Button>
                      <Button
                        variant="success" 
                        onClick={handleGenerateReport}
                        disabled={loading}
                        className="me-2"
                      >
                        <GetApp sx={{ fontSize: 18, mr: 0.5 }} />
                        {loading ? 'Generando...' : 'Generar Informe'}
                      </Button>
                      <Button
                        variant="danger" 
                        onClick={() => setShowDeletePracticaModal(true)}
                        disabled={loading}
                      >
                        <Delete sx={{ fontSize: 18, mr: 0.5 }} />
                        Borrar Práctica
                      </Button>
                    </Col>
                  </Row>
                ) : (
                  <Row>
                    <Col md={12}>
                      <Button
                        variant="success" 
                        onClick={handleGenerateReport}
                        disabled={loading}
                      >
                        <GetApp sx={{ fontSize: 18, mr: 0.5 }} />
                        {loading ? 'Generando...' : 'Generar Informe'}
                      </Button>
                    </Col>
                  </Row>
                )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Gestión de Archivos */}
        <div className="section-card">
          <div className="section-header">
            <h3 className="section-title">Informe</h3>
          </div>
          <div className="section-content">
            <Row>
              <Col md={12}>
                {user?.procesador === 'S' || user?.procesador === true ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Subir Adjuntos</Form.Label>
                      <Form.Control
                        type="file"
                        multiple
                        accept=".doc,.docx,.pdf"
                        onChange={handleFileSelect}
                        ref={fileInputRef}
                      />
                      <Form.Text className="text-muted">
                        Solo se permiten archivos DOC, DOCX y PDF
                      </Form.Text>
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary" 
                        onClick={() => handleUploadFiles(false)}
                        disabled={loading || selectedFiles.length === 0}
                      >
                        <Upload sx={{ fontSize: 18, mr: 0.5 }} />
                        Subir Adjuntos
                      </Button>
                      <Button
                        variant="success" 
                        onClick={() => handleUploadFiles(true)}
                        disabled={loading || selectedFiles.length === 0}
                      >
                        <CloudUpload sx={{ fontSize: 18, mr: 0.5 }} />
                        Subir como Informe
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="alert alert-info">
                    <strong>Acceso restringido:</strong> Solo los usuarios procesadores pueden adjuntar documentos.
                  </div>
                )}
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Estado del Turno</Form.Label>
                  <Form.Select
                    name="idEstadoTurno"
                    value={formData.idEstadoTurno}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (e.target.value !== '19') {
                        setFormData(prev => ({ ...prev, subEstado: '' }));
                      }
                    }}
                  >
                    <option value="">Seleccionar estado...</option>
                    {estadosTurno.length > 0 ? (
                      estadosTurno.map((estado) => (
                        <option key={estado.idEstadoTurno} value={estado.idEstadoTurno}>
                          {estado.estadoTurno}
                        </option>
                      ))
                    ) : (
                      <option disabled>No hay estados disponibles</option>
                    )}
                  </Form.Select>
                </Form.Group>
                {String(formData.idEstadoTurno) === '19' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Sub Estado</Form.Label>
                    <Form.Select
                      name="subEstado"
                      value={formData.subEstado || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar sub estado...</option>
                      <option value="Reporte CPAP y Estudio">Reporte CPAP y Estudio</option>
                      <option value="Reporte CPAP">Reporte CPAP</option>
                      <option value="Estudio">Estudio</option>
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Procesador</Form.Label>
                  <Form.Select
                    name="procesador"
                    value={formData.procesador}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar procesador...</option>
                    {procesadores.length > 0 ? (
                      procesadores.map((procesador) => (
                        <option key={procesador.id} value={procesador.id}>
                          {procesador.usuario1}
                        </option>
                      ))
                    ) : (
                      <option disabled>No hay procesadores disponibles</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            {adjuntosEeg.length > 0 && (
              <Row>
                <Col md={12}>
                  <h6>Archivos EEG</h6>
                  <div className="adjuntos-list">
                    {adjuntosEeg.map((adjunto, index) => (
                      <div key={index} className="adjunto-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDescargarEeg(adjunto.idAdjunto);
                          }}
                          className="text-decoration-none btn btn-link p-0"
                          style={{ textAlign: 'left' }}
                          disabled={descargandoEeg === adjunto.idAdjunto}
                        >
                          {descargandoEeg === adjunto.idAdjunto 
                            ? 'Generando link de descarga...' 
                            : adjunto.nombre}
                        </Button>
                        {descargandoEeg === adjunto.idAdjunto 
                          ? <span className="spinner-border spinner-border-sm text-dark" role="status" aria-label="Descargando" />
                          : <Badge bg="dark">EEG</Badge>}
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            )}

            {adjuntos.filter(adj => !adj.esInforme && adj.tipo !== 'T').length > 0 && (
              <Row>
                <Col md={12}>
                  <h6>Archivos para Subir</h6>
                  <div className="adjuntos-list">
                    {adjuntos.filter(adj => !adj.esInforme && adj.tipo !== 'T').map((adjunto, index) => (
                      <div key={index} className="adjunto-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedDoc({ url: adjuntosAPI.getPublicUrl(adjunto.nombre), name: adjunto.nombre });
                            setShowDocViewer(true);
                          }}
                          className="text-decoration-none btn btn-link p-0"
                          style={{ textAlign: 'left' }}
                        >
                          {adjunto.nombre}
                        </Button>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="secondary">Adjunto</Badge>
                          {(user?.procesador === 'S' || user?.procesador === true) && (
                            <button 
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setAdjuntoToDelete(adjunto.idAdjunto);
                                setShowDeleteModal(true);
                              }}
                              disabled={loading}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '18px',
                                padding: '4px',
                                opacity: loading ? 0.5 : 1,
                                transition: 'transform 0.1s',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              title="Eliminar archivo"
                            >
                              ❌
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
              </Row> 
            )}
            
            <Row className="mt-3">
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Observación</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={nuevaObservacion}
                      onChange={(e) => setNuevaObservacion(e.target.value)}
                      placeholder="Ingrese una observación..."
                    />
                    <Button
                      variant="primary" 
                      onClick={handleGuardarObservacion}
                      disabled={loading}
                    >
                      <Chat sx={{ fontSize: 18, mr: 0.5 }} />
                      Guardar
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                {observaciones.length > 0 && (
                  <div className="observaciones-container">
                    <h6 className="observaciones-title">Observaciones</h6>
                    <div className="table-container-obs">
                      <DataTable className="data-table-obs">
                        <thead>
                          <tr>
                            <th>Observación</th>
                            <th>Usuario</th>
                            <th>Fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {observaciones.map((obs, index) => (
                            <tr key={index}>
                              <td>{obs.observacion}</td>
                              <td>{obs.usrAlta}</td>
                              <td>
                                {obs.fechaAlta ? new Date(obs.fechaAlta).toLocaleString('es-AR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </DataTable>
                    </div>
                  </div>
                )}
              </Col>
            </Row>

          </div>
        </div>

        <div className="actions-section">
          <Button
            type="submit" 
            className="btn-primary-action"
            disabled={loading}
            sx={{ bgcolor: '#3b82f6 !important', color: '#fff !important', '&:hover': { bgcolor: '#2563eb !important' } }}
          >
            <Save sx={{ fontSize: 18, mr: 0.5 }} />
            {loading ? 'Guardando...' : 'Actualizar Turno'}
          </Button>
          <Button
            className="btn-secondary-action"
            onClick={() => {
              window.scrollTo(0, 0);
              onVolver();
            }}
            disabled={loading}
          >
            <ArrowBack sx={{ fontSize: 18, mr: 0.5 }} />
            Volver
          </Button>
        </div>
      </Form>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Vista Previa del Informe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ height: '70vh' }}>
            {previewUrl && (
              <iframe
                src={previewUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Vista previa del informe"
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro de eliminar este adjunto?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteAdjunto}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeletePracticaModal} onHide={() => setShowDeletePracticaModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro de eliminar la práctica?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeletePracticaModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={async () => {
            setShowDeletePracticaModal(false);
            setLoading(true);
            try {
              await practicasAPI.eliminarPractica(turno.idTurno);
              showToast('Práctica eliminada correctamente', 'success');
              setReportGenerated(false);
              setPreviewUrl('');
            } catch (err) {
              showToast('Error al eliminar la práctica', 'danger');
            } finally {
              setLoading(false);
            }
          }}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
      
        <Footer />
      </div>
    </>
  );
};

export default EditarPractica;